import {
  createColumnHelper,
  type AccessorFnColumnDef,
  type Row,
  type RowData,
  type TableFeatures,
} from '@tanstack/react-table';
import type { JSX } from 'react';
import { useIntl } from 'react-intl';

import Borders from '@dynatrace/strato-design-tokens/borders';
import Spacings from '@dynatrace/strato-design-tokens/spacings';
import {
  ChevronDownSmallIcon,
  ChevronRightSmallIcon,
} from '@dynatrace/strato-icons';

import { Button } from '../../../../buttons/button/Button.js';
import { BuiltinColumnHeaderName } from '../../components/BuiltinColumnHeaderName.js';
import { DataTableDefaultCell } from '../../components/DataTableDefaultCell.js';
import { DATA_TABLE_ROW_DETAILS_COLUMN_ID } from '../../constants.js';
import { dataTableMessages } from '../../messages.js';
import type { TDataTableFeatures } from '../types/features.types.js';

const rowDetailsColumnHelper = createColumnHelper<
  TDataTableFeatures,
  RowData
>();

function RowDetailsCell<TFeatures extends TableFeatures, TData extends RowData>(
  props: Readonly<{ row: Row<TFeatures, TData> }>,
): JSX.Element {
  const { row } = props;
  const intl = useIntl();

  const isOpen = row.getIsDetailsOpen();
  const Icon = isOpen ? <ChevronDownSmallIcon /> : <ChevronRightSmallIcon />;

  return (
    <DataTableDefaultCell>
      <Button
        size="condensed"
        variant="default"
        style={{
          borderRadius: Borders.Radius.Field.Subdued,
          paddingLeft: Spacings.Size2,
          paddingRight: Spacings.Size2,
          paddingTop: 0,
          paddingBottom: 0,
        }}
        aria-label={intl.formatMessage(dataTableMessages.rowDetailsTrigger, {
          isDetailsOpen: isOpen,
          id: row.id,
        })}
        aria-expanded={isOpen}
        aria-controls={isOpen ? row.getRowDetailsAriaControlsId() : undefined}
        onClick={() => row.toggleDetails()}
        disabled={!row.getCanOpenDetails()}
      >
        <Button.Prefix>{Icon}</Button.Prefix>
      </Button>
    </DataTableDefaultCell>
  );
}

/**
 * Column definition for the row details column.
 * @internal
 */
export const ROW_DETAILS_COLUMN: AccessorFnColumnDef<
  TDataTableFeatures,
  RowData,
  string
> = rowDetailsColumnHelper.accessor(() => '', {
  id: DATA_TABLE_ROW_DETAILS_COLUMN_ID,
  alignment: 'left',
  header: () => (
    <BuiltinColumnHeaderName message={dataTableMessages.rowDetailsColumnName} />
  ),
  width: 'content',
  enableSorting: false,
  enableResizing: false,
  enableUserActions: false,
  enableHiding: false,
  meta: {
    builtin: true,
    builtinPlacement: 'prefix',
    hasCustomCellRenderer: true,
    hasCustomHeaderRenderer: true,
    cellDeps: (cell) => {
      return [cell.row.getIsDetailsOpen()];
    },
  },
  cell: ({ row }) => <RowDetailsCell row={row} />,
});
